import {
  collectorNotices,
  collectorTotal,
  GRADE_OPTIONS,
  helpKey,
  isFieldRequired,
  labelKey,
  optionKey,
  specifyKey,
  specifyOption,
  validateCollectorSection,
  visibleFields,
  type CollectorCategory,
  type CollectorContext,
  type CollectorField,
  type CollectorSectionId,
  type CollectorValue,
  type CollectorValues,
} from "@kovernow/flows/add_item";
import { Checkbox } from "@kovernow/react-ui/components/checkbox";
import { InfoBanner } from "@kovernow/react-ui/components/info-banner";
import { Label } from "@kovernow/react-ui/components/label";
import { RadioGroup, RadioGroupItem } from "@kovernow/react-ui/components/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@kovernow/react-ui/components/select";
import { useId, useState, type RefObject } from "react";
import { FormField } from "../../components/FormField";
import { useServices } from "../../core/app-context";
import { useT } from "../../core/use-t";
import { STEP_HEADING_ID, StepActions, StepHeading, type ServerFieldErrors } from "./shared";

export type CollectorFormSection = Exclude<CollectorSectionId, "documents">;

/** The advisory notices for `placement`, as polite banners (never blocking). */
export function AdvisoryNotices({
  category,
  values,
  placement,
  today,
}: {
  category: CollectorCategory;
  values: CollectorValues;
  placement: "info" | "submit";
  today: string;
}) {
  const t = useT();
  const { config } = useServices();
  const notices = collectorNotices(category, values, config.currency, today).advisory.filter((n) => n.placement === placement);
  return (
    <div aria-live="polite" className="flex flex-col gap-3" data-testid={`notices-${placement}`}>
      {notices.map((n) => (
        <InfoBanner key={n.id} tone="info" title={t(n.titleKey)} body={t(n.bodyKey)} data-testid={`notice-${n.id}`} />
      ))}
    </div>
  );
}

/**
 * One section of a collector schema (info, grading or storage): every
 * visible field, rendered by type, validated by flows. Continue is
 * `aria-disabled` while the section is invalid (the trading-card cap
 * included); pressing it then shows every field error.
 */
export function CollectorSectionStep({
  category,
  sectionId,
  values,
  setValues,
  ctx,
  headingRef,
  serverErrors,
  clearServerError,
  onBack,
  onContinue,
  continueLabel,
  showSubmitNotices,
}: {
  category: CollectorCategory;
  sectionId: CollectorFormSection;
  values: CollectorValues;
  setValues: (update: (v: CollectorValues) => CollectorValues) => void;
  ctx: CollectorContext;
  headingRef: RefObject<HTMLHeadingElement | null>;
  serverErrors: ServerFieldErrors;
  clearServerError: (field: string) => void;
  onBack?: () => void;
  onContinue: () => void;
  continueLabel?: string;
  /** The last section of an edit: the submit-time notices show above Save. */
  showSubmitNotices?: boolean;
}) {
  const t = useT();
  const { format } = useServices();
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [showAll, setShowAll] = useState(false);
  const errors = validateCollectorSection(category, sectionId, values, ctx);
  const invalid = Object.keys(errors).length > 0;

  const errorFor = (key: string): string | undefined => {
    const server = serverErrors[key];
    if (server) return t(server);
    const e = errors[key];
    if (!e) return undefined;
    // The cap blocks at once; other errors after a blur or a Continue.
    if (e === "collector.notices.cap") {
      const cap = collectorNotices(category, values, ctx.currency, ctx.today).cap;
      return t(e, { cap: format.currency(cap?.cap ?? 0) });
    }
    return showAll || touched[key] ? t(e) : undefined;
  };
  const set = (key: string, value: CollectorValue) => {
    setValues((v) => ({ ...v, [key]: value }));
    clearServerError(key);
  };
  const touch = (key: string) => () => setTouched((s) => ({ ...s, [key]: true }));

  return (
    <section aria-labelledby={STEP_HEADING_ID} className="flex flex-col gap-5" data-testid={`step-${sectionId}`}>
      <StepHeading headingRef={headingRef}>{t(`addItem.steps.${sectionId}`)}</StepHeading>
      {visibleFields(category, sectionId, values).map((field) => (
        <CollectorFieldInput
          key={field.key}
          category={category}
          field={field}
          values={values}
          today={ctx.today}
          required={isFieldRequired(field, values)}
          error={errorFor(field.key)}
          specifyError={errorFor(specifyKey(field.key))}
          onChange={set}
          onBlur={touch}
        />
      ))}
      {sectionId === "info" ? <AdvisoryNotices category={category} values={values} placement="info" today={ctx.today} /> : null}
      {showSubmitNotices ? <AdvisoryNotices category={category} values={values} placement="submit" today={ctx.today} /> : null}
      <StepActions
        onBack={onBack}
        invalid={invalid}
        onInvalid={() => setShowAll(true)}
        onContinue={onContinue}
        continueLabel={continueLabel}
      />
    </section>
  );
}

function str(v: CollectorValue | undefined): string {
  return typeof v === "string" ? v : "";
}

/** One schema field, by type, with its specify input when a chosen option reveals one. */
function CollectorFieldInput({
  category,
  field,
  values,
  today,
  required,
  error,
  specifyError,
  onChange,
  onBlur,
}: {
  category: CollectorCategory;
  field: CollectorField;
  values: CollectorValues;
  today: string;
  required: boolean;
  error?: string;
  specifyError?: string;
  onChange: (key: string, value: CollectorValue) => void;
  onBlur: (key: string) => () => void;
}) {
  const t = useT();
  const { config, format } = useServices();
  const id = useId();
  const labelId = `${id}-label`;
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const value = values[field.key];
  const baseLabel = t(labelKey(category, field.key));
  const label = field.type === "currency" ? t("collector.withCurrency", { label: baseLabel, currency: config.currency }) : baseLabel;
  const hintText =
    [required ? null : t("collector.optional"), field.help ? t(helpKey(category, field.key)) : null, field.type === "multi" && !field.help ? t("collector.selectAll") : null]
      .filter(Boolean)
      .join(" ") || undefined;
  const testId = `field-${field.key}`;
  const describedBy = [hintText ? hintId : null, error ? errorId : null].filter(Boolean).join(" ") || undefined;

  const opt = specifyOption(field, values);
  const specify = opt ? (
    <FormField
      label={
        opt.specify === "amount"
          ? t("collector.withCurrency", { label: t("collector.specifyLabel", { label: baseLabel }), currency: config.currency })
          : t("collector.specifyLabel", { label: baseLabel })
      }
      inputMode={opt.specify === "amount" ? "decimal" : opt.specify === "integer" ? "numeric" : undefined}
      autoComplete="off"
      required={opt.specify !== true}
      value={str(values[specifyKey(field.key)])}
      error={specifyError}
      onBlur={onBlur(specifyKey(field.key))}
      onChange={(e) => onChange(specifyKey(field.key), e.target.value)}
      data-testid={`field-${specifyKey(field.key)}`}
    />
  ) : null;

  const errorLine = error ? (
    <p id={errorId} className="text-sm font-semibold text-status-error">
      {error}
    </p>
  ) : null;
  const hintLine = hintText ? (
    <p id={hintId} className="text-sm text-muted-foreground">
      {hintText}
    </p>
  ) : null;

  switch (field.type) {
    case "text":
    case "number":
    case "currency":
    case "date":
      return (
        <FormField
          label={label}
          hint={hintText}
          type={field.type === "date" ? "date" : "text"}
          inputMode={field.type === "text" || field.type === "date" ? undefined : "decimal"}
          max={field.type === "date" ? today : undefined}
          autoComplete="off"
          required={required}
          value={str(value)}
          error={error}
          onBlur={onBlur(field.key)}
          onChange={(e) => onChange(field.key, e.target.value)}
          data-testid={testId}
        />
      );
    case "total": {
      const total = collectorTotal(values);
      return (
        <dl className="flex flex-col gap-1 rounded-lg border p-4" data-testid={testId}>
          <dt className="text-sm text-muted-foreground">{label}</dt>
          <dd className="text-lg font-semibold">{format.currency(total ?? 0)}</dd>
        </dl>
      );
    }
    case "select":
    case "grade": {
      const triggerId = `${id}-trigger`;
      const options = field.type === "grade" ? GRADE_OPTIONS : (field.options ?? []).map((o) => o.value);
      return (
        <div className="flex flex-col gap-1.5">
          <Label id={labelId} htmlFor={triggerId}>
            {label}
          </Label>
          {hintLine}
          <Select
            value={str(value) || undefined}
            onValueChange={(v) => {
              onChange(field.key, v);
              onBlur(field.key)();
            }}
          >
            <SelectTrigger
              id={triggerId}
              aria-labelledby={`${labelId} ${triggerId}`}
              aria-describedby={describedBy}
              aria-invalid={error ? true : undefined}
              aria-required={required || undefined}
              className="min-h-11 w-full sm:w-72"
              data-testid={testId}
            >
              <SelectValue placeholder={t("collector.choose")} />
            </SelectTrigger>
            <SelectContent>
              {options.map((o) => (
                <SelectItem key={o} value={o}>
                  {field.type === "grade" ? format.number(Number(o)) : t(optionKey(category, field, o))}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errorLine}
          {specify}
        </div>
      );
    }
    case "multi": {
      const chosen = Array.isArray(value) ? value : [];
      return (
        <fieldset className="flex flex-col gap-2" aria-describedby={describedBy} data-testid={testId}>
          <legend className="mb-1 font-semibold">{label}</legend>
          {hintLine}
          {(field.options ?? []).map((o) => {
            const cid = `${id}-${o.value}`;
            return (
              <div key={o.value} className="flex min-h-11 items-center gap-2">
                <Checkbox
                  id={cid}
                  checked={chosen.includes(o.value)}
                  aria-invalid={error ? true : undefined}
                  onCheckedChange={(on) => {
                    onChange(field.key, on === true ? [...chosen.filter((x) => x !== o.value), o.value] : chosen.filter((x) => x !== o.value));
                    onBlur(field.key)();
                  }}
                />
                <Label htmlFor={cid}>{t(optionKey(category, field, o.value))}</Label>
              </div>
            );
          })}
          {errorLine}
          {specify}
        </fieldset>
      );
    }
    default: {
      // radio, yesNo
      return (
        <div className="flex flex-col gap-2" data-testid={testId}>
          <p id={labelId} className="font-semibold">
            {label}
          </p>
          {hintLine}
          <RadioGroup
            aria-labelledby={labelId}
            aria-describedby={describedBy}
            aria-invalid={error ? true : undefined}
            aria-required={required || undefined}
            value={str(value)}
            onValueChange={(v) => {
              onChange(field.key, v);
              onBlur(field.key)();
            }}
            className={field.type === "yesNo" ? "flex flex-wrap gap-4" : undefined}
          >
            {(field.options ?? []).map((o) => (
              <div key={o.value} className="flex min-h-11 items-center gap-2">
                <RadioGroupItem value={o.value} id={`${id}-${o.value}`} />
                <Label htmlFor={`${id}-${o.value}`}>{t(optionKey(category, field, o.value))}</Label>
              </div>
            ))}
          </RadioGroup>
          {errorLine}
          {specify}
        </div>
      );
    }
  }
}
